// Structured API error model (PRD §16): code, message, retryable, correlation_id.
// Errors never carry credentials.
import { z } from "zod";

export const ERROR_CODES = [
  "bad_request",
  "unauthenticated",
  "mfa_required",
  "forbidden",
  "not_found",
  "version_conflict",
  "idempotency_conflict",
  "invalid_requirement",
  "validation_failed",
  "gate_pending",
  "policy_denied",
  "rate_limited",
  "unavailable",
  "internal",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    retryable: z.boolean(),
    correlation_id: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/** HTTP status for each error code (409 conflicts, 422 invalid requirement, 429 retry policy). */
export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  bad_request: 400,
  unauthenticated: 401,
  mfa_required: 401,
  forbidden: 403,
  not_found: 404,
  version_conflict: 409,
  idempotency_conflict: 409,
  invalid_requirement: 422,
  validation_failed: 422,
  gate_pending: 409,
  policy_denied: 403,
  rate_limited: 429,
  unavailable: 503,
  internal: 500,
};

/** Error raised by services and mapped by the API layer. */
export class DomainError extends Error {
  readonly code: ErrorCode;
  readonly retryable: boolean;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, message: string, options?: { retryable?: boolean; details?: Record<string, unknown> }) {
    super(message);
    this.name = "DomainError";
    this.code = code;
    this.retryable = options?.retryable ?? false;
    this.details = options?.details;
  }
}

/** Response of asynchronous commands: 202 + operation_id + status_url. */
export const acceptedOperationSchema = z.object({
  operation_id: z.uuid(),
  status: z.enum(["accepted", "running", "succeeded", "failed"]),
  status_url: z.string(),
});
export type AcceptedOperation = z.infer<typeof acceptedOperationSchema>;
